// Nyilvános cégadatok (SiteSettings) megjelenítési segédei — az ÁSZF, az adatkezelési
// tájékoztató, a kapcsolat oldal és a lábléc ugyanígy állítja össze a cégadatokat.
// A webshop (márka) neve (company_name) és a cég jogi neve (company_legal_name) külön mező.
import type { PublicSiteSettings } from '../services/emailMarketingService';

type SiteInfo = Partial<PublicSiteSettings> | null | undefined;

const clean = (value: string | null | undefined): string => (value ?? '').trim();

/** Állandó márkanév-tartalék (első renderelés / betöltés közben / üres beállításnál). */
export const BRAND_NAME_FALLBACK = 'GT Kreatív Sarok';

/** A webshop (márka) neve a beállításokból; üresen / betöltés közben az állandó tartalék. */
export const brandName = (site: SiteInfo): string => clean(site?.company_name) || BRAND_NAME_FALLBACK;

/** A cég jogi neve; ha még nincs kitöltve, a webshop neve (hogy a jogi szöveg ne legyen alany nélkül). */
export const companyLegalName = (site: SiteInfo): string =>
  clean(site?.company_legal_name) || clean(site?.company_name);

/** `tel:` link a kiírt telefonszámból (szóközök, kötőjelek, zárójelek nélkül). Üres → ''. */
export const telHref = (phone: string | null | undefined): string => {
  const digits = clean(phone).replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : '';
};

/** Magyar határozott névelő a név elé: „az" magánhangzóval kezdődő név előtt, egyébként „a".
 *  Üres névnél csak a névelő („a"). `capitalize` — mondat elején „Az"/„A". */
export const definiteArticleFor = (name: string, capitalize = false): string => {
  const article = /^[aáeéiíoóöőuúüű]/i.test(clean(name)) ? 'az' : 'a';
  return capitalize ? article.charAt(0).toUpperCase() + article.slice(1) : article;
};

export const withDefiniteArticle = (name: string, capitalize = false): string => {
  const text = clean(name);
  const article = definiteArticleFor(text, capitalize);
  return text ? `${article} ${text}` : article;
};

/** Postacím: „Jogi név, cím" — a hiányzó rész kimarad. */
export const companyPostalAddress = (site: SiteInfo): string =>
  [companyLegalName(site), clean(site?.company_address)].filter(Boolean).join(', ');

/** Cégjogi azonosítók egy sorban (kapcsolat oldal): jogi név, cégjegyzékszám, adószám —
 *  csak a kitöltöttek, a webshop nevére NEM esik vissza. Üres, ha egyik sincs megadva. */
export const companyRegistryLine = (site: SiteInfo): string => {
  const reg = clean(site?.company_registration_number);
  const tax = clean(site?.company_tax_number);
  return [
    clean(site?.company_legal_name),
    reg ? `Cégjegyzékszám: ${reg}` : '',
    tax ? `Adószám: ${tax}` : '',
  ].filter(Boolean).join(' · ');
};

/** A lábléc cégadat-sora: jogi név, székhely, adószám, cégjegyzékszám, e-mail, telefon
 *  — csak a kitöltött mezők. */
export const footerCompanyParts = (site: SiteInfo): string[] => {
  const tax = clean(site?.company_tax_number);
  const reg = clean(site?.company_registration_number);
  return [
    companyLegalName(site),
    clean(site?.company_address),
    tax ? `Adószám: ${tax}` : '',
    reg ? `Cégjegyzékszám: ${reg}` : '',
    clean(site?.company_email),
    clean(site?.company_phone),
  ].filter(Boolean);
};
